import { useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from '../../components/common/Sidebar';
import PlanSubmissionForm from '../../components/teacher/PlanSubmissionForm';
import PlanTable from '../../components/teacher/PlanTable';
import ActivityPanel from '../../components/teacher/ActivityPanel';
import PlanPreviewModal from '../../components/teacher/PlanPreviewModal';
import NoticeToast from '../../components/common/NoticeToast';
import { getPlans, submitPlan } from '../../api/teacher';
import { useAuth } from '../../auth/AuthContext';
import { BRAND_ASSETS } from '../../branding';
import { apiUrl } from '../../config/api';
import { useNotice } from '../../hooks/useNotice';
import { getFileExtension, openOrDownloadDocument, safeFilename } from '../../utils/documentHandling';
import './TeacherDashboard.css';

const STATUS_OPTIONS = ['Tous', 'En analyse', 'Validé', 'Non validé'];
const FILE_BASE = apiUrl();
const GRID_BASE = apiUrl('/api/plans');
const ITEMS_PER_PAGE = 5;

export default function TeacherDashboard() {
  const { user, token, logout } = useAuth();
  const { notice, showNotice, clearNotice } = useNotice();
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [error, setError] = useState('');
  const [view, setView] = useState('list');
  const [statusFilter, setStatusFilter] = useState('Tous');
  const [courseFilter, setCourseFilter] = useState('Tous');
  const [dateFilter, setDateFilter] = useState('Toutes');
  const [sessionFilter, setSessionFilter] = useState('Toutes');
  const [programmeFilter, setProgrammeFilter] = useState('Tous');
  const [modeFilter, setModeFilter] = useState('Tous');
  const [campusFilter, setCampusFilter] = useState('Tous');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSection, setActiveSection] = useState('plans');
  const [previewPlan, setPreviewPlan] = useState(null);
  const plansSectionRef = useRef(null);
  const submitSectionRef = useRef(null);

  const handleOpenResult = async (plan) => {
    try {
      const response = await fetch(plan.resultUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } else if (response.status === 404) {
        showNotice('Resultat non disponible: la grille de validation n\'a pas encore ete generee.', 'warning');
      } else {
        showNotice('Erreur lors du chargement du resultat.', 'error');
      }
    } catch (err) {
      showNotice('Resultat non disponible: la grille de validation n\'a pas encore ete generee.', 'warning');
    }
  };

  const handlePreviewPlan = (plan) => {
    openOrDownloadDocument({
      url: plan.pdfUrl,
      fileName: plan.rawFilename,
      onOpenPdf: () => setPreviewPlan(plan),
      onNotify: showNotice,
    });
  };

  useEffect(() => {
    const loadPlans = async () => {
      if (!user?.id) return;
      setLoading(true);
      setError('');
      try {
        const data = await getPlans(user.id);
        setPlans(data);
      } catch (err) {
        setError(err.message || 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, [user?.id, refreshToken]);

  const normalizedPlans = useMemo(() => {
    return plans.map((plan) => {
      const statusMap = {
        'non_analyse': 'en analyse',
        'analyse': 'en analyse',
        'validé': 'validé',
        'non validé': 'non validé',
        'correction demandée': 'en révision',
        'rejeté': 'non validé',
      };
      const rawStatus = (plan.statut || '').toLowerCase();
      const mappedStatus = statusMap[rawStatus] || rawStatus || 'en analyse';
      const title = plan.nom_fichier ? plan.nom_fichier.replace(/\.(pdf|doc|docx)$/i, '') : plan.cours;
      const safeName = safeFilename(plan.nom_fichier);
      const fileExtension = getFileExtension(plan.nom_fichier);
      return {
        id: plan.id,
        title: title || 'Plan de cours',
        rawFilename: plan.nom_fichier || '',
        fileExtension,
        course: plan.cours || 'N/A',
        status: mappedStatus,
        submittedAt: (plan.date_upload || '').slice(0, 10),
        lastUpdated: (plan.updated_at || plan.date_upload || '').slice(0, 10),
        chef: plan.chef_nom || '—',
        pdfStatus: plan.nom_fichier ? 'Téléchargé' : '—',
        pdfUrl: plan.nom_fichier ? `${FILE_BASE}/static/plans/${plan.id}_${safeName}` : '#',
        resultUrl: `${GRID_BASE}/${plan.id}/grille`,
        session: plan.session || 'N/A',
        departement: plan.departement || 'N/A',
        programme: plan.programme || 'N/A',
        mode_enseignement: plan.mode_enseignement || 'N/A',
        campus: plan.campus || 'N/A',
      };
    });
  }, [plans]);

  const courses = useMemo(() => {
    const unique = new Set(normalizedPlans.map((plan) => plan.course));
    return ['Tous', ...Array.from(unique)];
  }, [normalizedPlans]);

  const sessions = useMemo(() => {
    const unique = new Set(normalizedPlans.map((plan) => plan.session).filter(s => s && s !== 'N/A'));
    return ['Toutes', ...Array.from(unique)];
  }, [normalizedPlans]);

  const programmes = useMemo(() => {
    const unique = new Set(normalizedPlans.map((plan) => plan.programme).filter(p => p && p !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [normalizedPlans]);

  const modes = useMemo(() => {
    const unique = new Set(normalizedPlans.map((plan) => plan.mode_enseignement).filter(m => m && m !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [normalizedPlans]);

  const campus = useMemo(() => {
    const unique = new Set(normalizedPlans.map((plan) => plan.campus).filter(c => c && c !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [normalizedPlans]);

  const filteredPlans = useMemo(() => {
    const now = new Date();
    const filtered = normalizedPlans.filter((plan) => {
      const matchesStatus =
        statusFilter === 'Tous' || plan.status.toLowerCase() === statusFilter.toLowerCase();
      const matchesCourse = courseFilter === 'Tous' || plan.course === courseFilter;
      const matchesSession = sessionFilter === 'Toutes' || plan.session === sessionFilter;
      const matchesProgramme = programmeFilter === 'Tous' || plan.programme === programmeFilter;
      const matchesMode = modeFilter === 'Tous' || plan.mode_enseignement === modeFilter;
      const matchesCampus = campusFilter === 'Tous' || plan.campus === campusFilter;
      const matchesSearch =
        plan.title.toLowerCase().includes(search.toLowerCase()) ||
        plan.course.toLowerCase().includes(search.toLowerCase());

      let matchesDate = true;
      if (dateFilter !== 'Toutes') {
        const days = Number(dateFilter.replace('j', ''));
        const submitted = new Date(plan.submittedAt);
        const diff = (now - submitted) / (1000 * 60 * 60 * 24);
        matchesDate = diff <= days;
      }

      return matchesStatus && matchesCourse && matchesSession && 
             matchesProgramme && matchesMode && matchesCampus && matchesSearch && matchesDate;
    });

    // Afficher les plans les plus recents en premier
    return filtered.sort((a, b) => {
      const dateA = new Date(a.lastUpdated || a.submittedAt || 0).getTime();
      const dateB = new Date(b.lastUpdated || b.submittedAt || 0).getTime();

      if (dateB !== dateA) {
        return dateB - dateA;
      }

      return b.id - a.id;
    });
  }, [normalizedPlans, statusFilter, courseFilter, sessionFilter,
      programmeFilter, modeFilter, campusFilter, dateFilter, search]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredPlans.length / ITEMS_PER_PAGE));
  }, [filteredPlans.length]);

  const paginatedPlans = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPlans.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPlans, currentPage]);

  useEffect(() => {
    // Revenir a la premiere page quand les filtres changent
    setCurrentPage(1);
  }, [statusFilter, courseFilter, dateFilter, sessionFilter, programmeFilter, modeFilter, campusFilter, search]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const activities = useMemo(() => {
    return normalizedPlans.slice(0, 6).map((plan) => {
      const status = (plan.status || '').toLowerCase();
      if (status.includes('validé') || status.includes('approuvé')) {
        return {
          type: 'success',
          message: `Plan "${plan.title}" validé.`,
          time: plan.lastUpdated || plan.submittedAt || 'Date inconnue',
        };
      }

      if (status.includes('non validé') || status.includes('rejeté')) {
        return {
          type: 'warning',
          message: `Plan "${plan.title}" demande des corrections.`,
          time: plan.lastUpdated || plan.submittedAt || 'Date inconnue',
        };
      }

      if (status.includes('analyse')) {
        return {
          type: 'info',
          message: `Plan "${plan.title}" est en cours d'analyse.`,
          time: plan.lastUpdated || plan.submittedAt || 'Date inconnue',
        };
      }

      return {
        type: 'info',
        message: `Plan "${plan.title}" soumis pour revision.`,
        time: plan.submittedAt || plan.lastUpdated || 'Date inconnue',
      };
    });
  }, [normalizedPlans]);

  const handleUpload = async (payload) => {
    if (!user?.id) return;
    payload.append('teacher_id', user.id);
    payload.append('cours', payload.get('classe_nom') || '');
    await submitPlan(payload);
    const data = await getPlans(user.id);
    setPlans(data);
  };

  const handleNavigate = (sectionKey) => {
    setActiveSection(sectionKey);
    const sectionMap = {
      plans: plansSectionRef.current,
      submit: submitSectionRef.current,
    };

    sectionMap[sectionKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRefresh = () => {
    setRefreshToken((current) => current + 1);
  };

  useEffect(() => {
    const recent = normalizedPlans.slice(0, 3).map((plan) => ({
      id: plan.id,
      title: plan.title,
      date: plan.submittedAt,
    }));
    setHistory(recent);
  }, [normalizedPlans]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          if (entry.target === submitSectionRef.current) {
            setActiveSection('submit');
          }

          if (entry.target === plansSectionRef.current) {
            setActiveSection('plans');
          }
        });
      },
      {
        threshold: 0.35,
      }
    );

    if (plansSectionRef.current) {
      observer.observe(plansSectionRef.current);
    }

    if (submitSectionRef.current) {
      observer.observe(submitSectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="teacher-dashboard">
      <Sidebar active={activeSection} onNavigate={handleNavigate} history={history} />

      <section className="teacher-main">
        <header className="dashboard-header">
          <div className="header-brand-group">
            <img className="header-brand-icon" src={BRAND_ASSETS.iconMini} alt="" aria-hidden="true" />
            <div>
              <h2>Tableau de bord Enseignant</h2>
              <p className="muted">Gérez vos plans et suivez leur validation.</p>
            </div>
          </div>
          <div className="header-user">
            <button
              className="teacher-refresh-btn"
              onClick={handleRefresh}
              type="button"
              disabled={loading}
            >
              {loading ? 'Rafraîchissement...' : 'Rafraîchir'}
            </button>
            <span>{user?.nom || 'Utilisateur'}</span>
            <div className="avatar">{user?.nom?.slice(0, 1) || 'U'}</div>
            <button
              className="teacher-logout-btn"
              onClick={logout}
              type="button"
            >
              Deconnexion
            </button>
          </div>
        </header>

        <div className="filters">
          <input
            className="search-box"
            placeholder="Rechercher un plan..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                Statut: {status}
              </option>
            ))}
          </select>
          <select value={sessionFilter} onChange={(event) => setSessionFilter(event.target.value)}>
            {sessions.map((session) => (
              <option key={session} value={session}>
                Session: {session}
              </option>
            ))}
          </select>
          <select value={courseFilter} onChange={(event) => setCourseFilter(event.target.value)}>
            {courses.map((course) => (
              <option key={course} value={course}>
                Cours: {course}
              </option>
            ))}
          </select>
          <select value={programmeFilter} onChange={(event) => setProgrammeFilter(event.target.value)}>
            {programmes.map((prog) => (
              <option key={prog} value={prog}>
                Programme: {prog}
              </option>
            ))}
          </select>
          <select value={modeFilter} onChange={(event) => setModeFilter(event.target.value)}>
            {modes.map((mode) => (
              <option key={mode} value={mode}>
                Mode: {mode}
              </option>
            ))}
          </select>
          <select value={campusFilter} onChange={(event) => setCampusFilter(event.target.value)}>
            {campus.map((camp) => (
              <option key={camp} value={camp}>
                Campus: {camp}
              </option>
            ))}
          </select>
          <select value={dateFilter} onChange={(event) => setDateFilter(event.target.value)}>
            <option value="Toutes">Date: Toutes</option>
            <option value="7j">7 jours</option>
            <option value="30j">30 jours</option>
            <option value="90j">90 jours</option>
          </select>
          <button
            className="primary-button"
            type="button"
            onClick={() => handleNavigate('submit')}
          >
            + Soumettre un nouveau plan
          </button>
        </div>

        <div id="plans-section" ref={plansSectionRef}>
          {loading ? (
            <p className="muted">Chargement des plans...</p>
          ) : error ? (
            <p className="error">{error}</p>
          ) : (
            <PlanTable
              plans={paginatedPlans}
              view={view}
              onToggleView={setView}
              onOpenPreview={handlePreviewPlan}
              onOpenResult={handleOpenResult}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>

        <div id="submit-section" ref={submitSectionRef}>
          <PlanSubmissionForm onSubmit={handleUpload} />
        </div>
      </section>

      <aside className="teacher-panel">
        <ActivityPanel activities={activities} />
      </aside>

      {previewPlan && <PlanPreviewModal plan={previewPlan} onClose={() => setPreviewPlan(null)} />}
      <NoticeToast notice={notice} onClose={clearNotice} />
    </div>
  );
}
